import { useId, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { RecordStatus } from '@traceio-org/ui';
import { formatDate, formatDateTime, presentStatus, productShortName } from '../format';

/** A status word with the library's status presentation; the tone only supplements the text. */
export function StatusBadge({ status }: { status: string }) {
  const { label, tone } = presentStatus(status);
  return <RecordStatus tone={tone}>{label}</RecordStatus>;
}

/** Products of an organization in a dense cell: the product, and its status when it is not active. */
export function ProductSummary({
  products,
}: {
  products: readonly { productKey: string; status: string }[];
}) {
  if (products.length === 0) return <span className="admin-muted">None</span>;
  return (
    <ul className="admin-inline-list">
      {products.map((product) => {
        const status = presentStatus(product.status);
        return (
          <li key={product.productKey}>
            {productShortName(product.productKey)}
            {product.status === 'active' ? null : (
              <span className="admin-muted"> ({status.label.toLowerCase()})</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** A date (UTC) in the viewer's locale, with the exact time on hover and the ISO value for machines. */
export function DateText({ iso }: { iso: string }) {
  return (
    <time dateTime={iso} title={formatDateTime(iso)}>
      {formatDate(iso)}
    </time>
  );
}

/**
 * True while the element is wider inside than it is on screen. Without ResizeObserver (old browsers, jsdom)
 * it stays true, the safe answer for a region that may have to be scrolled with the keyboard.
 */
function useOverflowsHorizontally(ref: RefObject<HTMLElement | null>): boolean {
  const [overflows, setOverflows] = useState(true);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const measure = () => setOverflows(element.scrollWidth > element.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    if (element.firstElementChild) observer.observe(element.firstElementChild);
    return () => observer.disconnect();
  }, [ref]);
  return overflows;
}

/**
 * A data table that scrolls sideways inside its own region when it is wider than the page, never the page
 * itself. The region is named by the table caption, and it takes keyboard focus only while it actually
 * scrolls, so the arrow keys can move it on a phone without adding a tab stop on a wide screen.
 */
export function ScrollTable({
  caption,
  className,
  busy = false,
  children,
}: {
  caption: string;
  className: string;
  busy?: boolean;
  children: ReactNode;
}) {
  const captionId = useId();
  const ref = useRef<HTMLDivElement>(null);
  const scrollable = useOverflowsHorizontally(ref);
  return (
    <div
      ref={ref}
      className="admin-table-scroll"
      role="region"
      aria-labelledby={captionId}
      aria-busy={busy || undefined}
      tabIndex={scrollable ? 0 : undefined}
    >
      <table className={className}>
        <caption id={captionId} className="admin-sr-only">
          {caption}
        </caption>
        {children}
      </table>
    </div>
  );
}

/** A moment with its time of day (UTC), for records where minutes matter (audit events, support access). */
export function DateTimeText({ iso }: { iso: string }) {
  return <time dateTime={iso}>{formatDateTime(iso)}</time>;
}
